/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import _ from 'lodash';
import { Navigate, useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { store } from 'redux/store';

import EquipmentSite from './equipment-site';
import PageHeader from '@components/page-header';
import PageContainer from '@components/page-container';
import EquipmentCard from '@components/equipment-card';
import NewTabs from '@components/new-tabs';
import Icon from '@components/icon-component';
import EquipmentListing from '@components/equipment-listing';
import EquipmentBookmark from '@components/equipment-listing/equipment-bookmark';
import PermissionRestrict from 'hoc/permission-restrict';
import WorkOrder from '../../site-details/tasks/work-order';
import LoadingRound from '@components/loading-round/loading-round';
import EquipmentDetailsMoreInfo from './equipment-details-more-info';
import BookmarkErrorScreen from '@components/bookmark-error-screen';

import { PERMISSIONS } from 'permissions/constants';
import { log } from '@utilities/logger.util';
import { getAppDispatch } from '@utilities/dispatch.util';
import { backHistoryActions } from '@redux/slices/back-history';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { filterActions } from '@redux/slices/filters';
import { initWebWorker } from 'webworker';

import './equipment-site.scss';
import style from './style.module.scss';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';
import DeleteModal from '@components/modal/variants/delete-modal';
import { decodeToken } from '@utilities/authentication';
import { cacheActions } from '@redux/slices/cache';

const EquipmentSiteDetails = () => {
  const [searchParams] = useSearchParams();
  const dispatch = getAppDispatch();
  const { width } = useWindowDimensions();
  const navigate = useNavigate();
  const { ToasterUtil } = useToastContext();

  const swipeAbleMenuRef = useRef(null);
  const mainDivRef = useRef(null);
  const siteCardRef = useRef(null);
  const assetDetailPageHeaderRef = useRef(null);

  const [tab, setTab] = useState('0');
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editEquipment, setEditEquipment] = useState(false);
  const [workRequest, setWorkRequest] = useState(false);
  const [deleteBookmark, setDeleteBookmark] = useState(false);

  const [allTask, setAllTask] = useState(false);
  const [checks, setChecks] = useState(false);
  const [inspections, setInspections] = useState(false);
  const [workOrder, setWorkOrder] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [newOrEditAssets, setNewOrEditAssets] = useState(false);

  const [equipmentDetails, setEquipmentDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { sites: cacheSites = [], sitesMap = {} } = useSelector((state) => state.cache);
  const equipmentDetailsInfo = useSelector((state) => state.backHistory.equipmentDetails);
  const {
    tasksFilterCount = 0,
    psvsFilterCount = 0,
    pvsFilterCount = 0,
    equipmentFilterCount = 0
  } = useSelector((state) => state.filters);
  const [isFilterActive, setFilterActive] = useState(width < 768 ? false : true);
  const [isFilterActiveForChildEquipment, setFilterActiveForChildEquipment] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForPSV, setFilterActiveForPSV] = useState(width < 768 ? false : true);
  const [isFilterActiveForPVS, setFilterActiveForPVS] = useState(width < 768 ? false : true);

  const { tasksList: reduxTasksList = [], selectTask } = useSelector((state) => state.tasks);
  const { inspectionsList } = useSelector((state) => state.inspection);
  const { checksList } = useSelector((state) => state.checks);
  const { utFormDownloadingLoading } = useSelector((state) => state.utFormData) || {};

  const { id } = useParams();
  const { pathname, search } = useLocation();
  const bookmarkName = searchParams.get('bookmarkName');
  const bookmarkTab = searchParams.get('bookmarkTab');
  const bookmarkId = searchParams.get('bookmarkId');
  const isBookmarkAtAGlance = searchParams.get('isBookmarkAtAGlance') || false;

  const currentOpenTaskTab = searchParams.get('currentOpenTaskTab') || '';

  useEffect(() => {
    if (currentOpenTaskTab) {
      if (currentOpenTaskTab === 'allTask') setAllTask(true);
      else if (currentOpenTaskTab === 'workOrder') setWorkOrder(true);
      else if (currentOpenTaskTab === 'inspections') setInspections(true);
      else if (currentOpenTaskTab === 'checks') setChecks(true);
      else if (currentOpenTaskTab === 'workRequest') setWorkRequest(true);
      else if (currentOpenTaskTab === 'newOrEditAssets') setNewOrEditAssets(true);
    }
  }, [currentOpenTaskTab]);

  useEffect(() => {
    if (!_.isEmpty(equipmentDetailsInfo)) {
      equipmentDetailsInfo?.equipmentDetailsTab &&
        setTab(equipmentDetailsInfo?.equipmentDetailsTab || '0');
      equipmentDetailsInfo?.workOrder && setWorkOrder(equipmentDetailsInfo?.workOrder);
      equipmentDetailsInfo?.newOrEditAssets &&
        setNewOrEditAssets(equipmentDetailsInfo?.newOrEditAssets);
    }
  }, [equipmentDetailsInfo]);

  useEffect(() => {
    if (utFormDownloadingLoading) {
      return;
    }

    initWebWorker({
      args: {
        store: { ...store.getState() },
        equipmentId: id,
        getWorkRequestData: true,
        firstName: decodeToken().firstName,
        lastName: decodeToken().lastName,
        isParentEquipment: searchParams.get('from') === 'parent-equipment' ? true : false
      },
      type: 'get-single-equipment-details'
    })
      .then((data) => {
        setEquipmentDetails(data?.result);
        setIsLoading(false);
        setTab('0');
      })
      .catch((error) => {
        setIsLoading(false);
        setTab('0');
      });
  }, [id, syncComplete, reduxTasksList, inspectionsList, checksList, utFormDownloadingLoading]);

  const {
    equipmentsLoading,
    parentEquipment,
    tasks = [],
    allDoneTasks,
    allChecks,
    allDoneChecks,
    allInspections,
    allDoneInspections,
    allWorkOrders,
    allDoneWorkOrders,
    isCheckOverDue,
    isInspectionOverDue,
    isWorkOrderOverDue,
    isOverDue,
    children = [],
    psv = [],
    psvs = [],
    equipmentsMap,
    pvs = [],
    dataPoints,
    moreInfoDataPointsMap,
    category,
    type,
    siteId,
    data,
    status,
    name,
    link,
    newWorkRequestList,
    allOnDemandRecordCount,
    moreInfoDataPointsNew,
    moreInfoOfVessel,
    moreInfoOfPSVs,
    otherCategories,
    allEditEquipmentAssets,
    newOrEditedEquipmentOnDemandCount,
    notSHhowOnList = false
  } = React.useMemo(() => {
    return equipmentDetails;
  }, [equipmentDetails]);

  useEffect(() => {
    if (
      currentOpenTaskTab &&
      currentOpenTaskTab === 'newOrEditAssets' &&
      newOrEditAssets &&
      allEditEquipmentAssets?.length === 0
    ) {
      setNewOrEditAssets(false);
    }
  }, [currentOpenTaskTab, newOrEditAssets, allEditEquipmentAssets]);

  useEffect(() => {
    if (syncComplete) setSyncComplete(false);
  }, [syncComplete]);

  useMemo(() => {
    if (!isLoading) {
      if (!equipmentsLoading && !parentEquipment.equipmentId) return <Navigate to={'/equipment'} />;
      const { children: c, ...rest } = data;
      !dataPoints && log(c);
    }
  }, [equipmentDetails, isLoading]);

  const onParentClickEvent = ({ workOrderTab, workOrderSortTab }) => {
    dispatch?.(
      backHistoryActions.setEquipmentDetailsPageHistory({
        workOrder,
        equipmentDetailsTab: tab,
        workOrderTab,
        workOrderSortTab,
        newOrEditAssets
      })
    );
  };
  const editEquipmentBtnEvent = () => {
    const isNewEquipment = equipmentDetails?.data?.isNew ? 'true' : 'false';

    const equipmentAlreadyUpdated =
      equipmentDetails?.parentEquipment?.equipmentEditingStatus === 'Pending sync' ||
      equipmentDetails?.parentEquipment?.equipmentEditingStatus === 'In progress';

    const url = equipmentAlreadyUpdated
      ? `/add-edit-equipment?equipmentId=${id}&hideDiscardBtn=true&isNewEquipment=${isNewEquipment}`
      : `/add-edit-equipment?equipmentId=${id}&isNewEquipment=${isNewEquipment}`;

    navigate(url);
  };

  const handleInfoClick = () => {
    const infoTabIndex = items?.findIndex((x) => x?.label === 'Info');
    setTab(infoTabIndex);
  };

  const handleBookmarkClick = () => {
    setOpenModal(true);
  };

  const EquipmentCardCOmponent = ({
    moreInfoTabActive,
    showCard = false,
    className = '',
    isInfo = false
  }) =>
    showCard ? (
      <div className={`marginClass ${className}`} ref={siteCardRef}>
        <EquipmentCard
          equipmentEditingStatus={data?.equipmentEditingStatus || ''}
          equipmentDetails
          title={`${category} - ${type}`}
          subtitle={name}
          hideLocation={true}
          dataPoints={dataPoints}
          allTasksForSite={tasks}
          allDoneTasks={allDoneTasks}
          isOverDue={isOverDue}
          status={status}
          sites={{
            ...(cacheSites[sitesMap[siteId]] || {}),
            link: `/site-details/${siteId}`
          }}
          showChevronIcon={false}
          open={open}
          setOpen={setOpen}
          link={''}
          allOnDemandRecordCount={allOnDemandRecordCount + newOrEditedEquipmentOnDemandCount}
          onInfoCLick={handleInfoClick}
          onBookmarkCLick={handleBookmarkClick}
          infoTabActive={moreInfoTabActive}
          toDesktopLink={link && width >= 768 ? link : undefined}
          otherTabsView={
            allTask || checks || workOrder || workRequest || inspections || newOrEditAssets
          }
          hideBookmarkOnNewEquipment={equipmentDetails?.data?.isNew || false}
          childrenList={children}
          className={`${isInfo && style.borderBottomNone}`}
        />
      </div>
    ) : (
      <></>
    );

  const itemsMap = useMemo(() => {
    if (!isLoading)
      return [
        ...(tasks?.length > 0 || allOnDemandRecordCount > 0 || allEditEquipmentAssets?.length > 0
          ? [
              {
                label: `Tasks + records (${
                  tasks?.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
                })`,
                keyName: 'tasks'
              }
            ]
          : []),
        ...(children?.length > 0
          ? [
              {
                label: `Child equipment (${children.length})`,
                keyName: 'equipment'
              }
            ]
          : []),

        ...(psvs?.length > 0
          ? [
              {
                label: `PSVs (${psvs.length})`,
                keyName: 'psv'
              }
            ]
          : []),

        ...(pvs?.length > 0
          ? [
              {
                keyName: 'vessels'
              }
            ]
          : [])
      ];
    else return [];
  }, [isLoading]);

  const items = useMemo(() => {
    return !isLoading
      ? [
          ...(tasks?.length > 0 || allOnDemandRecordCount > 0 || allEditEquipmentAssets?.length > 0
            ? [
                {
                  label: `Tasks + records (${
                    tasks?.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
                  })`,
                  keyName: 'tasks',
                  children: (
                    <EquipmentSite
                      setChecks={setChecks}
                      setAllTask={setAllTask}
                      setWorkOrder={setWorkOrder}
                      setInspections={setInspections}
                      allWorkOrderForSite={allWorkOrders}
                      allDoneWorkOrder={allDoneWorkOrders}
                      allInspectionForSite={allInspections}
                      allCheckForSite={allChecks}
                      allDoneChecks={allDoneChecks}
                      allDoneInspection={allDoneInspections}
                      allTasksForSite={tasks}
                      allDoneTasks={allDoneTasks}
                      isOverDue={isOverDue}
                      isCheckOverDue={isCheckOverDue}
                      isCheck
                      isInspectionOverDue={isInspectionOverDue}
                      isWorkOrderOverDue={isWorkOrderOverDue}
                      newWorkRequestList={newWorkRequestList}
                      setWorkRequest={setWorkRequest}
                      allOnDemandRecordCount={
                        allOnDemandRecordCount + allEditEquipmentAssets?.length
                      }
                      newWorkOrderList={equipmentDetails?.newWorkOrderList || []}
                      newInspectionList={equipmentDetails?.newInspectionList || []}
                      newChecksList={equipmentDetails?.newChecksList || []}
                      allEditEquipmentAssets={allEditEquipmentAssets}
                      setNewOrEditAssets={setNewOrEditAssets}
                      card={() => (
                        <EquipmentCardCOmponent
                          moreInfoTabActive={false}
                          showCard={width <= 768 ? true : false}
                          className={`mb16 ${style.pt0}`}
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(children?.length > 0
            ? [
                {
                  label: `Child equipment (${children.length})`,
                  keyName: 'equipment',
                  children: (
                    <EquipmentListing
                      assetDetailsOfEquipmentLists={children}
                      isFilterActiveHeader={isFilterActiveForChildEquipment}
                      setFilterActiveHeader={setFilterActiveForChildEquipment}
                      from={pathname}
                      equipmentId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'equipment'}
                      navLabel="Child equipment"
                      noDistanceTab
                      card={() => (
                        <EquipmentCardCOmponent
                          moreInfoTabActive={false}
                          className={`${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(psvs?.length > 0
            ? [
                {
                  label: `PSVs (${psvs.length})`,
                  keyName: 'psv',
                  children: (
                    <EquipmentListing
                      assetDetailsOfEquipmentLists={psvs}
                      isFilterActiveHeader={isFilterActiveForPSV}
                      setFilterActiveHeader={setFilterActiveForPSV}
                      from={pathname}
                      psvs
                      equipmentId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'psv'}
                      navLabel="PSVs"
                      noDistanceTab
                      card={() => (
                        <EquipmentCardCOmponent
                          moreInfoTabActive={false}
                          className={`${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0 ||
                              children?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(pvs?.length > 0
            ? [
                {
                  label: `Vessels (${pvs.length})`,
                  keyName: 'vessels',
                  children: (
                    <EquipmentListing
                      assetDetailsOfEquipmentLists={pvs}
                      isFilterActiveHeader={isFilterActiveForPVS}
                      setFilterActiveHeader={setFilterActiveForPVS}
                      from={pathname}
                      pvs
                      equipmentId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'psvs'}
                      navLabel="Vessels"
                      noDistanceTab
                      card={() => (
                        <EquipmentCardCOmponent
                          moreInfoTabActive={false}
                          className={`${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0 ||
                              children?.length > 0 ||
                              psvs?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(moreInfoDataPointsMap
            ? [
                {
                  label: `Info`,
                  keyName: 'info',
                  children: (
                    <>
                      <div className={style.infoTab} id="equipment-info-tab">
                        <EquipmentCardCOmponent
                          moreInfoTabActive={true}
                          showCard={width <= 768 ? true : false}
                          className={` ${style.pt0} ${style.borderBottomNone}`}
                          isInfo
                        />

                        <EquipmentDetailsMoreInfo
                          moreInfoDataPointsMap={moreInfoDataPointsNew}
                          setEditEquipment={setEditEquipment}
                          moreInfoOfVessel={moreInfoOfVessel}
                          moreInfoOfPSVs={moreInfoOfPSVs}
                          otherCategories={otherCategories}
                          editEquipmentBtnEvent={editEquipmentBtnEvent}
                        />
                      </div>
                    </>
                  )
                }
              ]
            : [])
        ]
      : [];
  }, [
    isLoading,
    itemsMap,
    isFilterActive,
    isFilterActiveForChildEquipment,
    isFilterActiveForPSV,
    isFilterActiveForPVS,
    allDoneTasks,
    tasks,
    newWorkRequestList,
    allEditEquipmentAssets
  ]);

  const tabIsActive = useMemo(() => {
    return isFilterActiveForChildEquipment || isFilterActiveForPSV || isFilterActiveForPVS || false;
  }, [isFilterActiveForChildEquipment, isFilterActiveForPSV, isFilterActiveForPVS]);

  useEffect(() => {
    if (width <= 768) {
      const elements = document.getElementsByClassName('react-swipeable-view-container');

      for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        if (tasks?.length > 0) {
          if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
          if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
          if (tab === 3) element.style.transform = tabIsActive ? 'none' : 'translate(-300%, 0px)';
        } else {
          if (parseInt(tab) === 0)
            element.style.transform = tabIsActive ? 'none' : 'translate(0px, 0px)';
          if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
          if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
        }
      }
    }
  }, [tabIsActive, width]);

  useEffect(() => {
    if (items?.length > 0) {
      const params = new URLSearchParams(search);
      const currentTab = parseInt(tab);
      params.set('tab', currentTab);

      if (items[currentTab]?.keyName === 'tasks') {
        if (allTask) params.set('currentOpenTaskTab', 'allTask');
        else if (workOrder) params.set('currentOpenTaskTab', 'workOrder');
        else if (inspections) params.set('currentOpenTaskTab', 'inspections');
        else if (checks) params.set('currentOpenTaskTab', 'checks');
        else if (newOrEditAssets) params.set('currentOpenTaskTab', 'newOrEditAssets');
        else if (workRequest) params.set('currentOpenTaskTab', 'workRequest');
        else if (!workOrder && !allTask && !inspections && !checks && !newOrEditAssets)
          params.delete('currentOpenTaskTab');
      }

      params.set(
        'virtualScrollTab',
        currentTab === 0 && items[currentTab]?.keyName === 'tasks'
          ? -1
          : tasks?.length > 0
          ? currentTab - 1
          : currentTab
      );
      params.set('tab', tab);
      items[tab]?.keyName && params.set('keyName', items[tab]?.keyName);
      const tabParams = '?' + params.toString();
      const prevRoute = `${pathname}${tabParams}`;
      navigate(prevRoute, { replace: true });
    }
  }, [tab, items, workOrder, allTask, inspections, checks, workRequest, newOrEditAssets]);

  useEffect(() => {
    if (!isLoading && bookmarkTab) setTab(bookmarkTab);
  }, [isLoading, bookmarkTab]);

  const pageHeaderClickEvent = () => {
    workOrder && dispatch?.(filterActions.setTaskFilters(null));
    if (allTask) setAllTask(false);
    else if (checks) setChecks(false);
    else if (inspections) setInspections(false);
    else if (workOrder) setWorkOrder(false);
    else if (workRequest) setWorkRequest(false);
    else if (newOrEditAssets) setNewOrEditAssets(false);
    else navigate(-1);
    dispatch?.(backHistoryActions.setEquipmentDetailsPageHistory(null));
  };

  const pageHeaderTitle = useMemo(() => {
    return `${
      bookmarkName
        ? bookmarkName
        : allTask
        ? `Tasks, records (${
            tasks?.length > 0 || allOnDemandRecordCount || allEditEquipmentAssets?.length > 0
              ? tasks.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
              : ''
          })`
        : checks
        ? `Checks (${allChecks?.length > 0 ? allChecks?.length : ''})`
        : inspections
        ? `Inspections (${allInspections?.length > 0 ? allInspections.length : ''})`
        : workOrder
        ? `Work orders (${
            allWorkOrders?.length > 0 || equipmentDetails?.newWorkOrderList?.length
              ? allWorkOrders.length + equipmentDetails?.newWorkOrderList?.length
              : ''
          })`
        : workRequest
        ? `Work requests (${newWorkRequestList?.length > 0 ? newWorkRequestList?.length : ''})`
        : newOrEditAssets
        ? `New or edited assets (${
            allEditEquipmentAssets?.length > 0 ? allEditEquipmentAssets?.length : ''
          })`
        : searchParams.get('from') === 'parent-equipment'
        ? 'Parent equipment'
        : 'Equipment'
    }`;
  }, [
    bookmarkName,
    allTask,
    checks,
    allChecks,
    inspections,
    allInspections,
    workOrder,
    allWorkOrders,
    tasks,
    newWorkRequestList,
    workRequest,
    allOnDemandRecordCount,
    equipmentDetails,
    newOrEditAssets,
    allEditEquipmentAssets
  ]);

  const isFilterIconIsRequired = useMemo(() => {
    return (tab !== '0' &&
      !items[tab]?.keyName?.toLowerCase()?.includes('tasks') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('info')) ||
      allTask ||
      checks ||
      inspections ||
      workOrder ||
      workRequest ||
      newOrEditAssets
      ? items[tab]?.keyName === 'equipment'
        ? setFilterActiveForChildEquipment
        : items[tab]?.keyName === 'psv'
        ? setFilterActiveForPSV
        : items[tab]?.keyName === 'vessels'
        ? setFilterActiveForPVS
        : setFilterActive
      : false;
  }, [tab, items, allTask, checks, inspections, workOrder, workRequest, newOrEditAssets]);

  const filtersCount = useMemo(() => {
    return items[tab]?.keyName === 'equipment'
      ? equipmentFilterCount
      : items[tab]?.keyName === 'psv'
      ? psvsFilterCount
      : items[tab]?.keyName === 'vessels'
      ? pvsFilterCount
      : tasksFilterCount;
  }, [tab, items, equipmentFilterCount, psvsFilterCount, pvsFilterCount, tasksFilterCount]);

  const equipmentDetailsPageClassNames = useMemo(() => {
    return `${selectTask ? style.pageListingsWithMoreInfo : style.pageListings} ${style.noScroll} ${
      (workOrder || allTask || checks || inspections || workRequest || newOrEditAssets) &&
      style.noScroll
    } 
    ${
      (workOrder || allTask || checks || inspections || workRequest || newOrEditAssets) &&
      style.assetDetailsOnSmallScreen
    }`;
  }, [workOrder, allTask, checks, inspections, selectTask, workRequest, newOrEditAssets]);

  const equipmentNotFoundInBookmark = useMemo(() => {
    if (bookmarkName && !isLoading) {
      if (name && category && type) return false;
      else return true;
    }
    return false;
  }, [bookmarkName, isLoading, name, category, type]);

  const allInspectionsList = useMemo(() => {
    return [
      ...(equipmentDetails?.allInspections || []),
      ...(equipmentDetails?.newInspectionList || [])
    ];
  }, [inspections, equipmentDetails]);

  const allTasksList = useMemo(() => {
    return [
      ...(equipmentDetails?.tasks || []),
      ...(equipmentDetails.newWorkOrderList || []),
      ...(equipmentDetails?.newWorkRequestList || []),
      ...(equipmentDetails?.allEditEquipmentAssets || [])
    ];
  }, [allTask, equipmentDetails]);

  const allWorkOrderList = useMemo(() => {
    return [...(allWorkOrders || []), ...(equipmentDetails?.newWorkOrderList || [])];
  }, [workOrder, equipmentDetails, allWorkOrders]);

  const allChecksList = useMemo(() => {
    return [...(allChecks || []), ...(equipmentDetails?.newChecksList || [])];
  }, [checks, equipmentDetails, allChecks]);

  useEffect(() => {
    if (!isLoading && !name && !status && !notSHhowOnList) {
      navigate('/error?assetNotFound=true');
    } else if (!isLoading && notSHhowOnList) {
      //  I add this logic here because when a new equipment is sync it remove from list and
      //  modal is gone suddenly thats why I add the modal logic here for bookmarks load

      dispatch?.(cacheActions.updateSyncingModal(false));
      dispatch?.(cacheActions.closeCachingComplete());
      dispatch?.(cacheActions.updateRefreshingAndSyncingData(false));
      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      const timerId = setTimeout(() => {
        navigate(-1);
      }, 2000);

      return () => clearTimeout(timerId);
    }
  }, [isLoading, name, status, notSHhowOnList]);

  const onDeleteBookmark = async () => {
    try {
      if (!bookmarkId) {
        ToasterUtil.showError('Unable to delete bookmark: Bookmark ID is missing.');
        return;
      }

      await BookmarksService.deleteBookmarks({ bookmarkId: bookmarkId });

      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      ToasterUtil.showSuccess('Bookmark deleted successfully');

      const params = new URLSearchParams(location.search);

      params.set('from', 'site');
      params.delete('bookmarkName');
      params.delete('bookmarkId');

      setDeleteBookmark(false);

      const newUrl = `${location.pathname}?${params.toString()}`;

      // Use navigate with replace
      navigate(newUrl, { replace: true });
    } catch (error) {
      ToasterUtil.showError('Failed to delete bookmark');
    }
  };

  const { moreInfoTabActive, isTasksTabActive } = useMemo(() => {
    const parseTab = parseInt(tab);
    return {
      moreInfoTabActive: items[parseTab]?.keyName === 'info' ? true : false,
      isTasksTabActive: items[parseTab]?.keyName === 'tasks' ? true : false
    };
  }, [items, tab]);

  const swipeable = document.getElementById('react-swipeable-view-container') || null;
  const equipmentInfoTab = document.getElementById('equipment-info-tab') || null;
  const taskInfoTab = document.getElementById('tasks-info-tab') || null;

  useEffect(() => {
    if (swipeable) {
      if (moreInfoTabActive) {
        swipeable.style.background = '#ffffff';
      } else {
        swipeable.style.background = '';
      }
    }
  }, [
    // mainDivRef,
    // tab,
    moreInfoTabActive,
    swipeable
    // equipmentInfoTab,
    // taskInfoTab,
    // isTasksTabActive,
    // siteCardRef
  ]);

  const onScrollParentEvent = (event) => {
    const currentScrollPosition = event.target.scrollTop || 0;
    const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

    if (siteCardRef?.current) {
      const topHeaderHeight = width > 769 ? 94 : 86;
      const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
      const navigationTabsHeight = 33;
      const bottomFooterHeight = width > 769 ? 0 : 84;
      const totalHeight =
        topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

      const container =
        moreInfoTabActive && equipmentInfoTab
          ? equipmentInfoTab
          : isTasksTabActive && taskInfoTab
          ? taskInfoTab
          : null;
      if (container && (moreInfoTabActive || isTasksTabActive)) {
        container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
      }

      const containerIds = [
        'structure-listing-pages-container',
        'site-listing-pages-container',
        'pipeline-listing-pages-container',
        'equipment-listing-pages-container',
        'psvs-listing-pages-container',
        'pvs-listing-pages-container'
      ];

      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (container) {
          container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition + 10}px)`;
        }
      });
    }

    const tasksRecordsListsTabs = document?.querySelectorAll('#tasksRecordsListsTabs');
    for (let a = 0; a < tasksRecordsListsTabs.length; a++) {
      const totalHeight =
        tasksRecordsListsTabs[a]?.getAttribute('data-pageHeightOnDetailsPage') || 0;
      tasksRecordsListsTabs[a].style.height = `calc(100svh  - ${
        totalHeight - currentScrollPosition
      }px)`;
    }
  };

  return (
    <>
      {!isLoading && equipmentNotFoundInBookmark ? (
        <BookmarkErrorScreen />
      ) : (
        <PageContainer>
          <PageHeader
            disableBack={
              allTask ||
              checks ||
              inspections ||
              workOrder ||
              workRequest ||
              newOrEditAssets ||
              searchParams.get('from')
            }
            title={pageHeaderTitle}
            {...(isFilterIconIsRequired && {
              setFilterActive: isFilterIconIsRequired
            })}
            onClick={pageHeaderClickEvent}
            count={filtersCount}
            ref={assetDetailPageHeaderRef}
          />
          {isLoading ? (
            <LoadingRound className={style.loading} />
          ) : (
            <>
              <div
                ref={mainDivRef}
                id="assetDetailPage"
                className={equipmentDetailsPageClassNames}
                style={{ position: 'relative', paddingBottom: '0px', marginTop: '0px' }}
                onScroll={onScrollParentEvent}
              >
                <EquipmentCardCOmponent
                  moreInfoTabActive={moreInfoTabActive}
                  showCard={width > 768 ? true : false}
                />

                <div className="flex relative">
                  <div
                    style={{
                      marginTop: '0px',
                      overflow: 'hidden',
                      paddingBottom: '0px'
                    }}
                    className={`${style.pageListings} mobileMargin`}
                  >
                    {allTask ||
                    inspections ||
                    workOrder ||
                    checks ||
                    workRequest ||
                    newOrEditAssets ? (
                      <WorkOrder
                        workRequest={workRequest}
                        asset="equipment"
                        setSyncComplete={setSyncComplete}
                        tasks={
                          inspections
                            ? allInspectionsList
                            : allTask
                            ? allTasksList
                            : workOrder
                            ? allWorkOrderList
                            : checks
                            ? allChecksList
                            : workRequest
                            ? newWorkRequestList
                            : newOrEditAssets
                            ? allEditEquipmentAssets
                            : []
                        }
                        newOrEditAssets={newOrEditAssets}
                        isFilterActiveHeader={isFilterActive}
                        setFilterActiveHeader={setFilterActive}
                        selectTodoOrInProgressStatus={!_.isEmpty(bookmarkName) ? true : false}
                        onParentClickEvent={onParentClickEvent}
                        workOrderSelectedTab={equipmentDetailsInfo?.workOrderTab || null}
                        workOrderSortTab={equipmentDetailsInfo?.workOrderSortTab || null}
                        siteDetails
                        noDistanceTab
                        filterType={
                          allTask
                            ? 'all'
                            : inspections
                            ? 'inspection'
                            : workOrder
                            ? 'workOrder'
                            : checks
                            ? 'check'
                            : workRequest
                            ? 'workRequest'
                            : newOrEditAssets
                            ? 'newOrEditAssets'
                            : null
                        }
                      />
                    ) : items?.length > 0 ? (
                      <div
                        className={`assetDetailNavNotFixed`}
                        ref={swipeAbleMenuRef}
                        id="assetDetailsNavigation"
                      >
                        <NewTabs
                          active={tab}
                          tabs={items}
                          setActive={(tab) => {
                            setTab(tab);
                          }}
                          className="mobileMargin"
                          swipeAbleNavbarWhiteBackground={moreInfoTabActive ? true : false}
                        />
                      </div>
                    ) : (
                      <>
                        <div
                          className="no-result"
                          style={{ flex: 1, justifyContent: 'flex-start', gap: '20px' }}
                        >
                          <div>No results</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {openModal && (
                <EquipmentBookmark
                  open={openModal}
                  setOpen={(value) => {
                    setOpenModal(value);
                  }}
                  placeholder="My favourite truck"
                  type="Equipment Details"
                  equipmentId={id}
                  editMode={bookmarkId}
                  isBookmarkAtAGlance={isBookmarkAtAGlance}
                  bookmarkPrefillName={bookmarkName}
                  onDelete={() => {
                    setDeleteBookmark(true);
                    setOpenModal(false);
                  }}
                />
              )}
              {deleteBookmark && (
                <DeleteModal
                  open={deleteBookmark}
                  setOpen={setDeleteBookmark}
                  handleDelete={onDeleteBookmark}
                  confirmDeletionBookmark
                  recordType="Bookmark"
                />
              )}
            </>
          )}
        </PageContainer>
      )}
    </>
  );
};

export default React.memo(EquipmentSiteDetails);
